import { Actions, createEffect, ofType } from '@ngrx/effects';
import * as ObjectActions from './form-entry-configurable-fields.actions';
import { catchError, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import * as LogbookAppReducer from '../logbook.reducer';
import * as AppActions from '../app/actions';
import { IGetOneResponse } from '../../shared/model/interface/crud-response-interface.model';
import { FormEntryConfigurableFieldsService } from './form-entry-configurable-fields.service';
import {
  IConfigurableFieldsEditData,
  IConfigurableFieldsData,
  IConfigurableFieldsAddData,
} from './form-entry-configurable-fields.model';

@Injectable()
export class FormEntryConfigurableFieldsEffects {
  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<LogbookAppReducer.LogbookAppState>,
    private readonly formEntryConfigurableFieldsService: FormEntryConfigurableFieldsService,
  ) {}

  checkHasConfigurableFields = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.CHECK_HAS_CONFIGURABLE_FIELDS),
      switchMap((payload: ObjectActions.CheckHasConfigurableFields) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.formEntryConfigurableFieldsService
          .checkHasConfigurableFields(payload.logbookId, payload.formId, payload.issuerAndReason)
          .pipe(
            switchMap((response: IGetOneResponse<boolean>) => {
              return of(new ObjectActions.CheckHasConfigurableFieldsCompleted(response));
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.FetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getConfigurableFieldsAddData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.GET_FORM_ENTRY_CONFIGURABLE_FIELDS_ADD_DATA),
      switchMap((payload: ObjectActions.GetFormEntryConfigurableFieldsAddData) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.formEntryConfigurableFieldsService
          .getConfigurableFieldsAddData(payload.logbookId, payload.formId, payload.issuerAndReason)
          .pipe(
            switchMap((response: IGetOneResponse<IConfigurableFieldsAddData>) => {
              return of(
                new ObjectActions.GetFormEntryConfigurableFieldsAddDataCompleted(response),
                new AppActions.HideLoader(),
              );
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  createFormEntryWithConfigurableFields = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.CREATE_FORM_ENTRY_WITH_CONFIGURABLE_FIELDS),
      switchMap((payload: ObjectActions.CreateFormEntryWithConfigurableFields) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.formEntryConfigurableFieldsService
          .createFormEntryWithConfigurableFields(
            payload.logbookId,
            payload.formId,
            payload.fieldVersionId,
            payload.values,
            payload.issuerAndReason,
            payload.confirmCanceledUniqueValues,
          )
          .pipe(
            switchMap((response: IGetOneResponse<IConfigurableFieldsData>) => {
              return of(
                new ObjectActions.CreateFormEntryWithConfigurableFieldsCompleted(response),
                new AppActions.HideLoader(),
              );
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  getConfigurableFieldsEditData = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.GET_FORM_ENTRY_CONFIGURABLE_FIELDS_EDIT_DATA),
      switchMap((payload: ObjectActions.GetFormEntryConfigurableFieldsEditData) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.formEntryConfigurableFieldsService
          .getConfigurableFieldsEditData(payload.configurableFieldsDataId, payload.issuerAndReason)
          .pipe(
            switchMap((response: IGetOneResponse<IConfigurableFieldsEditData>) => {
              return of(
                new ObjectActions.GetFormEntryConfigurableFieldsEditDataCompleted(response),
                new AppActions.HideLoader(),
              );
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );

  updateFormEntryConfigurableFields = createEffect(() =>
    this.actions$.pipe(
      ofType(ObjectActions.UPDATE_FORM_ENTRY_CONFIGURABLE_FIELDS),
      switchMap((payload: ObjectActions.UpdateFormEntryConfigurableFields) => {
        this.store.dispatch(new AppActions.ShowLoader());

        return this.formEntryConfigurableFieldsService
          .updateFormEntryConfigurableFields(
            payload.configurableFieldsDataId,
            payload.values,
            payload.issuerAndReason,
            payload.confirmCanceledUniqueValues,
          )
          .pipe(
            switchMap((response: IGetOneResponse<IConfigurableFieldsData>) => {
              return of(
                new ObjectActions.UpdateFormEntryConfigurableFieldsCompleted(response),
                new AppActions.HideLoader(),
              );
            }),
            catchError((errorRes) => {
              return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
            }),
          );
      }),
      catchError((errorRes) => {
        return of(new ObjectActions.EntryFlowFetchError(errorRes), new AppActions.HideLoader());
      }),
    ),
  );
}
